import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { trpc } from '../lib/trpc';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Check, Loader2, Database, User, Settings as SettingsIcon } from 'lucide-react';

export default function Setup() {
  const [, navigate] = useLocation();
  const [step, setStep] = useState<'check' | 'migrations' | 'init' | 'done'>('check');
  const [migrationResults, setMigrationResults] = useState<string[]>([]);
  const [formData, setFormData] = useState({ email: '', password: '', name: '', companyName: '' });

  const checkMutation = trpc.setup.checkStatus.useQuery(undefined, { retry: false });
  const migrationMutation = trpc.setup.runMigrations.useMutation({
    onSuccess: (data) => {
      setMigrationResults(data.results);
      toast.success('Migrations abgeschlossen');
      setStep('init');
    },
    onError: (e) => toast.error(`Migration Fehler: ${e.message}`),
  });
  const initMutation = trpc.setup.initialize.useMutation({
    onSuccess: () => {
      toast.success('Setup abgeschlossen! Weiterleitung zum Login...');
      setStep('done');
      setTimeout(() => navigate('/login'), 2000);
    },
    onError: (e) => toast.error(`Init Fehler: ${e.message}`),
  });

  useEffect(() => {
    if (checkMutation.data?.needsSetup === false) {
      navigate('/login');
    }
  }, [checkMutation.data, navigate]);

  const runMigrations = () => {
    setStep('migrations');
    migrationMutation.mutate();
  };

  const initialize = () => {
    if (!formData.email || !formData.password || !formData.name) {
      toast.error('Bitte alle Pflichtfelder ausfüllen');
      return;
    }
    initMutation.mutate(formData);
  };

  if (checkMutation.isLoading) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50'>
        <Loader2 className='w-8 h-8 animate-spin text-purple-600' />
      </div>
    );
  }

  if (!checkMutation.data?.dbAvailable) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50 p-4'>
        <Card className='max-w-md'>
          <CardHeader>
            <CardTitle className='text-destructive'>Datenbankverbindung fehlgeschlagen</CardTitle>
            <CardDescription>
              Bitte prüfen Sie die DATABASE_URL Umgebungsvariable und starten Sie die Anwendung neu.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className='text-sm text-muted-foreground'>{checkMutation.data?.error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className='min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 p-4'>
      <Card className='max-w-2xl w-full shadow-2xl'>
        <CardHeader>
          <CardTitle className='text-3xl flex items-center gap-3'>
            <SettingsIcon className='w-8 h-8 text-purple-600' />
            Okulus Setup
          </CardTitle>
          <CardDescription>Willkommen! Richten Sie Ihre Okulus Installation ein.</CardDescription>
        </CardHeader>
        <CardContent className='space-y-6'>
          {/* Progress Steps */}
          <div className='flex items-center justify-between mb-8'>
            <div className={`flex items-center gap-2 ${step === 'check' || step === 'migrations' ? 'text-purple-600' : 'text-green-600'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'check' || step === 'migrations' ? 'bg-purple-100' : 'bg-green-100'}`}>
                {step === 'check' || step === 'migrations' ? <Database className='w-5 h-5'/> : <Check className='w-5 h-5'/>}
              </div>
              <span className='text-sm font-medium'>Datenbank</span>
            </div>
            <div className='flex-1 h-0.5 bg-muted mx-4' />
            <div className={`flex items-center gap-2 ${step === 'init' ? 'text-purple-600' : step === 'done' ? 'text-green-600' : 'text-muted-foreground'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'init' ? 'bg-purple-100' : step === 'done' ? 'bg-green-100' : 'bg-muted'}`}>
                {step === 'done' ? <Check className='w-5 h-5'/> : <User className='w-5 h-5'/>}
              </div>
              <span className='text-sm font-medium'>Admin</span>
            </div>
          </div>

          {/* Check & Migrations */}
          {(step === 'check' || step === 'migrations') && (
            <div className='space-y-4'>
              <div className='space-y-2'>
                <h3 className='font-semibold text-lg'>Datenbank Migrations</h3>
                <p className='text-sm text-muted-foreground'>Alle Datenbank-Tabellen und -Strukturen werden automatisch angelegt.</p>
              </div>
              {migrationResults.length > 0 && (
                <div className='rounded-md border p-4 max-h-60 overflow-y-auto bg-muted/30'>
                  <pre className='text-xs space-y-1'>
                    {migrationResults.map((r, i) => <div key={i}>{r}</div>)}
                  </pre>
                </div>
              )}
              <Button
                onClick={runMigrations}
                disabled={migrationMutation.isPending}
                className='w-full bg-gradient-to-r from-purple-600 to-pink-600'
              >
                {migrationMutation.isPending ? <><Loader2 className='w-4 h-4 mr-2 animate-spin'/>Migrations werden ausgeführt...</> : 'Migrations starten'}
              </Button>
            </div>
          )}

          {/* Initialize Admin */}
          {step === 'init' && (
            <div className='space-y-4'>
              <div className='space-y-2'>
                <h3 className='font-semibold text-lg'>Administrator-Account erstellen</h3>
                <p className='text-sm text-muted-foreground'>Dieser Account hat vollen Zugriff auf alle Funktionen.</p>
              </div>
              <div className='space-y-3'>
                <div>
                  <Label>Name *</Label>
                  <Input value={formData.name} onChange={e => setFormData(f => ({ ...f, name: e.target.value }))} placeholder='Max Mustermann' />
                </div>
                <div>
                  <Label>E-Mail *</Label>
                  <Input type='email' value={formData.email} onChange={e => setFormData(f => ({ ...f, email: e.target.value }))} placeholder='admin@example.com' />
                </div>
                <div>
                  <Label>Passwort * (mindestens 8 Zeichen)</Label>
                  <Input type='password' value={formData.password} onChange={e => setFormData(f => ({ ...f, password: e.target.value }))} placeholder='••••••••' />
                </div>
                <div>
                  <Label>Firmenname (optional)</Label>
                  <Input value={formData.companyName} onChange={e => setFormData(f => ({ ...f, companyName: e.target.value }))} placeholder='Meine Fotografie' />
                </div>
              </div>
              <Button
                onClick={initialize}
                disabled={initMutation.isPending}
                className='w-full bg-gradient-to-r from-purple-600 to-pink-600'
              >
                {initMutation.isPending ? <><Loader2 className='w-4 h-4 mr-2 animate-spin'/>Setup abschließen...</> : 'Setup abschließen'}
              </Button>
            </div>
          )}

          {/* Done */}
          {step === 'done' && (
            <div className='text-center space-y-4 py-8'>
              <div className='w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto'>
                <Check className='w-10 h-10 text-green-600' />
              </div>
              <h3 className='text-2xl font-bold text-green-600'>Setup erfolgreich!</h3>
              <p className='text-muted-foreground'>Sie werden zum Login weitergeleitet...</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
